import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Dropdown, Menu, Space, Popconfirm } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import { IUser, IUserPassInfo } from 'models';
import { userRoleLabel } from 'models/user/userRoleLabels';
import { Table, Tag } from 'components/layout';
import './users-table.scss';

const { Item } = Menu;

interface Props {
    data?: IUser[] | null;
    pageSize?: number;
    page: number;
    onChangePage: Function;
    total?: number;
    loading?: boolean;
    returnTo?: string;
    onDeleteUser: (id: string) => void;
    onBlockUser: (id: string) => void;
    onUnblockUser: (id: string) => void;
}

const UsersTable: FC<Props> = (props: Props) => {
    const {
        data,
        pageSize,
        page,
        onDeleteUser,
        onBlockUser,
        onUnblockUser,
        onChangePage,
        total,
        loading,
        returnTo,
    } = props;

    const navigate = useNavigate();

    const openUser = (id: string) => {
        navigate(`/user/${id}`, returnTo ? { state: { returnTo } } : undefined);
    };

    const onRowClick = (record: IUser) => (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        if (
            target.closest('.users-table-actions') ||
            target.closest('.ant-dropdown') ||
            target.closest('.ant-popover') ||
            target.closest('.ant-popconfirm')
        ) {
            return;
        }
        openUser(record.id);
    };

    const renderConfirmItem = (item: typeof items[number], row: IUser) => (
        <Item key={item.key} onClick={({ domEvent }) => domEvent.stopPropagation()}>
            <Popconfirm
                title={item.titleConfirm}
                onConfirm={() => item.action(row.id)}
                placement="topRight"
                okText="Да"
                cancelText="Нет"
            >
                <span className="users-table-action-label">{item.label}</span>
            </Popconfirm>
        </Item>
    );

    const items = [
        { key: 'more', needConfirm: false, label: 'Подробнее', action: openUser },
        {
            key: 'delete',
            needConfirm: true,
            titleConfirm: 'Вы уверены что хотите удалить пользователя?',
            label: 'Удалить',
            action: onDeleteUser,
        },
        {
            key: 'block',
            needConfirm: true,
            titleConfirm: 'Вы уверены что хотите заблокировать пользователя?',
            label: 'Заблокировать',
            action: onBlockUser,
        },
        {
            key: 'unblock',
            needConfirm: true,
            titleConfirm: 'Вы уверены что хотите разблокировать пользователя?',
            label: 'Разблокировать',
            action: onUnblockUser,
        },
    ];

    const columns = [
        {
            title: 'ФИО',
            dataIndex: 'name',
            width: '15%',
            render: (_: string, row: IUser) => {
                return `${row.lastName} ${row.firstName} ${row.middleName}`;
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            width: '15%',
        },
        {
            title: '№ телефона',
            dataIndex: 'phone',
            width: '15%',
        },
        {
            title: 'Компания',
            dataIndex: 'organization',
            width: '10%',
        },
        {
            title: 'Должность',
            dataIndex: 'position',
            width: '10%',
        },
        {
            title: 'Пропуска до:',
            dataIndex: 'passInfo',
            render: (passInfo: IUserPassInfo) => {
                return passInfo?.passExpirationDateTime
                    ? dayjs(passInfo?.passExpirationDateTime).utc().format('DD.MM.YYYY HH:mm')
                    : null;
            },
            width: '15%',
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            render: (_: string, user: IUser) => {
                return (
                    <div>
                        {user?.role?.name === 'CD_RESIDENT' && (
                            <div>
                                <div>{userRoleLabel(user?.role?.name)}</div>
                                <div>
                                    До {dayjs(user?.offerExpirationDate).format('DD.MM.YYYY')}
                                </div>
                            </div>
                        )}
                        {user?.role?.name !== 'CD_RESIDENT' && (
                            <div>
                                <div>{userRoleLabel(user?.role?.name)}</div>
                            </div>
                        )}
                    </div>
                );
            },
            width: '10%',
        },
        {
            title: 'Статус',
            dataIndex: 'userStatus',
            render: (_: string, row: IUser) => {
                return (
                    <Tag
                        type={row.userStatus === 'ACTIVE' ? 'success' : 'error'}
                        key={`${row.id}_userStatus`}
                    >
                        {row.userStatus}
                    </Tag>
                );
            },
            width: '10%',
        },
        {
            title: 'Действия',
            dataIndex: 'actions',
            render: (_: string, row: IUser) => (
                <div className="users-table-actions">
                    <Dropdown
                        className="dropdown"
                        trigger={['click']}
                        overlay={
                            <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
                                {items.map((item) => {
                                    if (item.needConfirm) {
                                        if (row.userStatus === 'ACTIVE' && item.key === 'block') {
                                            return renderConfirmItem(item, row);
                                        }
                                        if (
                                            row.userStatus === 'BLOCKED' &&
                                            item.key === 'unblock'
                                        ) {
                                            return renderConfirmItem(item, row);
                                        }
                                        if (
                                            (row.userStatus === 'ACTIVE' ||
                                                row.userStatus === 'BLOCKED') &&
                                            item.key === 'delete'
                                        ) {
                                            return renderConfirmItem(item, row);
                                        }
                                    } else {
                                        return (
                                            <Item
                                                key={item.key}
                                                onClick={({ domEvent }) => {
                                                    domEvent.stopPropagation();
                                                    item.action(row.id);
                                                }}
                                            >
                                                {item.label}
                                            </Item>
                                        );
                                    }

                                    return null;
                                })}
                            </Menu>
                        }
                    >
                        <Space>
                            Действия...
                            <DownOutlined />
                        </Space>
                    </Dropdown>
                </div>
            ),
            width: '10%',
        },
    ];
    return (
        <Table
            className="users-table"
            data={data}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            columns={columns}
            onChangePage={onChangePage}
            onRow={onRowClick}
        />
    );
};

export default UsersTable;
